import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLElement} [subscriptionNote] - NOVOLD: 定期便価格の副次表示（blocks/price.liquid）
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote, subscriptionNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

    // NOVOLD: [ref="subscriptionNote"] とその newPrice に対する前後関係を、newPrice を
    // 動かす（replaceWith）前に読んでおく（2026-09-06）。blocks/price.liquid が定期便カードを
    // render 'price' より前に置くようになったため、新規生成時の挿入位置を「価格の後ろ固定」
    // ではなく実際の並び順に合わせる必要がある。newPrice を先に動かすと newProductPrice 内での
    // 相対位置が失われる（別ツリーに移動してしまう）ため、判定はここで済ませておく。
    const newSubNote = newProductPrice.querySelector('[ref="subscriptionNote"]');
    const subNoteBeforePrice = !!(
      newSubNote &&
      newPrice &&
      newSubNote.compareDocumentPosition(newPrice) & Node.DOCUMENT_POSITION_FOLLOWING
    );

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // NOVOLD: Update subscription note (blocks/price.liquid の副次表示)。
    // 定期便プランを持たない商品では [ref="subscriptionNote"] 自体が存在しないため no-op。
    if (!newSubNote) {
      subscriptionNote?.remove();
    } else if (!subscriptionNote) {
      // 新規生成時は、上で判定しておいた並び順（価格の前 or 後）どおりに挿入する。
      const clone = /** @type {Element} */ (newSubNote.cloneNode(true));
      if (subNoteBeforePrice) {
        newPrice?.insertAdjacentElement('beforebegin', clone);
      } else {
        newPrice?.insertAdjacentElement('afterend', clone);
      }
    } else {
      subscriptionNote.replaceWith(newSubNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
